import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Globe, Search, Share2, MessageSquare, Target, Sparkles,
  CheckCircle2, AlertTriangle, XCircle, Loader2, Download
} from "lucide-react";
import axios from "axios";
import { useState } from "react";
import { toast } from "sonner";
import { useTrialLock } from "../hooks/useTrialLock";
import { blockExportIfTrial } from "./TrialLock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { key: "web_presence", label: "Web Presence", icon: Globe },
  { key: "local_seo", label: "Local SEO", icon: Search },
  { key: "social_media", label: "Social Media", icon: Share2 },
  { key: "reviews", label: "Reviews", icon: MessageSquare },
  { key: "opportunities", label: "Opportunities", icon: Target },
  { key: "value_proposition", label: "Value Proposition", icon: Sparkles },
];

function StatusIcon({ status }) {
  if (status === "good") return <CheckCircle2 className="w-4 h-4 text-[#22C55E]" />;
  if (status === "critical") return <XCircle className="w-4 h-4 text-[#EF4444]" />;
  return <AlertTriangle className="w-4 h-4 text-[#F97316]" />;
}

export default function AuditModal({ open, onOpenChange, lead, audit, loading }) {
  const [downloading, setDownloading] = useState(false);
  const { onTrial } = useTrialLock();

  const downloadPdf = async () => {
    if (blockExportIfTrial(onTrial, "download PDF audit")) return;
    if (!lead || !audit) return;
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API}/audit-pdf`,
        {
          lead_name: lead.name || "Lead",
          lead_address: lead.address || "",
          lead_website: lead.website || "",
          lead_phone: lead.phone_international || lead.phone || "",
          lead_sector: lead.sector || "",
          digital_score: lead.digital_score || 0,
          badge: lead.badge || "",
          audit,
          lang: (window.localStorage.getItem("alp.lang") === "pt") ? "pt" : "en",
        },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (lead.name || "audit").replace(/[^a-z0-9]+/gi, "_");
      a.download = `audit_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (_e) {
      toast.error("PDF download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="audit-modal"
        className="max-w-3xl bg-white p-0 overflow-hidden rounded-xl border-[#E5E7EB] backdrop-blur-sm"
      >
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-[#E5E7EB]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="text-[18px] font-bold text-[#111827]">
                Digital Audit — {lead?.name}
              </DialogTitle>
              <p className="text-[12px] text-[#6B7280] mt-1">
                AI-generated 6-section audit · Score {lead?.digital_score}/100 · {lead?.badge}
              </p>
            </div>
            <button
              type="button"
              onClick={downloadPdf}
              disabled={loading || !audit || downloading}
              data-testid="audit-download-pdf"
              className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#0A1233] hover:bg-[#1B3A8A] text-white text-[12px] font-semibold disabled:opacity-50"
              title="Download white-label PDF"
            >
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              PDF
            </button>
          </div>
        </DialogHeader>

        <div className="px-6 py-5 max-h-[65vh] overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-[#6B7280]">
              <Loader2 className="w-7 h-7 animate-spin text-[#3B82F6]" />
              <p className="text-[13px]">Generating audit with GPT…</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                const sec = audit?.[s.key] || { status: "warning", detail: "—" };
                return (
                  <div
                    key={s.key}
                    data-testid={`audit-section-${s.key}`}
                    className="border border-[#E5E7EB] rounded-lg p-4 bg-[#FAFAFB] hover:bg-white transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-[#3B82F6]" />
                        <span className="text-[13px] font-semibold text-[#111827]">{s.label}</span>
                      </div>
                      <StatusIcon status={sec.status} />
                    </div>
                    <p className="text-[12.5px] text-[#4B5563] leading-relaxed">
                      {sec.detail || "No data."}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
